import { Basket, BasketItem } from '@/models/Basket';
import { DeliveryData, DeliveryTimeSlot } from '@/models/DeliveryData';
import { encryptAES, decryptAES } from './crypto/AES';
import {
  readMigratedItem,
  removeMigratedItem,
  writeMigratedItem,
} from './crypto/storageMigration';
import { STORAGE_KEY } from './storageKeys';
import { safeLocalStorage } from './safeStorage';

export interface DistrictRemediationItem {
  productId: number;
  count: number;
  title: string;
  url: string;
  imageUrl?: string;
  isExtra?: boolean;
}

export interface DistrictRemediationState {
  active: boolean;
  targetDistrictId: number;
  targetDistrictTitle: string;
  lockedDate: string;
  lockedDeliveryTimeId: number;
  lockedDeliveryTimeTitle: string;
  lockedDeliveryTimeStart?: string;
  lockedDeliveryTimeEnd?: string;
  pendingProductIds: number[];
  savedItems: DistrictRemediationItem[];
}

export const getDistrictRemediationState = (): DistrictRemediationState | undefined => {
  if (typeof window === 'undefined') return undefined;
  const raw = readMigratedItem(safeLocalStorage, STORAGE_KEY.DISTRICT_REMEDIATION);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(decryptAES(raw)) as DistrictRemediationState;
    if (!parsed?.active || !Array.isArray(parsed.pendingProductIds)) {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
};

export const setDistrictRemediationState = (state?: DistrictRemediationState) => {
  if (typeof window === 'undefined') return;
  if (!state) {
    removeMigratedItem(safeLocalStorage, STORAGE_KEY.DISTRICT_REMEDIATION);
    notifyDistrictRemediationChanged();
    return;
  }
  writeMigratedItem(
    safeLocalStorage,
    STORAGE_KEY.DISTRICT_REMEDIATION,
    encryptAES(JSON.stringify(state)),
  );
  notifyDistrictRemediationChanged();
};

export const isDistrictRemediationActive = () => Boolean(getDistrictRemediationState()?.active);

export const startDistrictRemediation = ({
  basket,
  targetDistrictId,
  targetDistrictTitle,
}: {
  basket: Basket;
  targetDistrictId: number;
  targetDistrictTitle: string;
}) => {
  const savedItems: DistrictRemediationItem[] = (basket.items || []).map((item: BasketItem) => ({
    productId: item.id,
    count: item.count,
    title: item.title,
    url: item.url,
    imageUrl: item.imageUrl,
    isExtra: item.isExtra,
  }));

  setDistrictRemediationState({
    active: true,
    targetDistrictId,
    targetDistrictTitle,
    lockedDate: basket.deliveryTime.date,
    lockedDeliveryTimeId: basket.deliveryTime.id,
    lockedDeliveryTimeTitle: basket.deliveryTime.title,
    lockedDeliveryTimeStart: basket.deliveryTime.start,
    lockedDeliveryTimeEnd: basket.deliveryTime.end,
    pendingProductIds: savedItems.map((item) => item.productId),
    savedItems,
  });
};

export const getPendingRemediationItems = (): DistrictRemediationItem[] => {
  const state = getDistrictRemediationState();
  if (!state?.active) return [];
  const pending = new Set(state.pendingProductIds);
  return state.savedItems.filter((item) => pending.has(item.productId));
};

export const markDistrictRemediationProductResolved = (productId: number) => {
  const state = getDistrictRemediationState();
  if (!state) return;
  const pendingProductIds = state.pendingProductIds.filter((id) => id !== productId);
  const savedItems = state.savedItems.filter((item) => item.productId !== productId);
  if (pendingProductIds.length === 0) {
    setDistrictRemediationState(undefined);
    return;
  }
  setDistrictRemediationState({
    ...state,
    pendingProductIds,
    savedItems,
  });
};

export const dismissDistrictRemediationProduct = (productId: number) => {
  markDistrictRemediationProductResolved(productId);
};

export const clearDistrictRemediation = () => {
  setDistrictRemediationState(undefined);
};

export const DISTRICT_REMEDIATION_CHANGED = 'DISTRICT_REMEDIATION_CHANGED';

function notifyDistrictRemediationChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(DISTRICT_REMEDIATION_CHANGED));
}

function slotMatchesLockedTime(
  slot: DeliveryTimeSlot,
  lockedTitle: string,
  lockedStart?: string,
  lockedEnd?: string,
) {
  const normalizedTitle = lockedTitle?.trim().toLowerCase();
  if (normalizedTitle && slot.title?.trim().toLowerCase() === normalizedTitle) {
    return true;
  }
  if (lockedStart && lockedEnd && slot.start === lockedStart && slot.end === lockedEnd) {
    return true;
  }
  return false;
}

export function resolveDeliverySlotFromData(
  deliveryData: DeliveryData | undefined,
  lockedDate: string,
  lockedTitle: string,
  lockedStart?: string,
  lockedEnd?: string,
): { date: string; deliveryTimeId: number } | null {
  if (!deliveryData?.schemas?.length || !lockedDate) {
    return null;
  }

  const dateAvailable =
    deliveryData.dates?.some((item) => item.date === lockedDate) ||
    lockedDate >= new Date().toISOString().slice(0, 10);

  if (!dateAvailable) {
    return null;
  }

  for (const schema of deliveryData.schemas) {
    const slots = [...(schema.deliveryTimes || []), ...(schema.directButtons || [])];
    const matched = slots.find((slot) =>
      slotMatchesLockedTime(slot, lockedTitle, lockedStart, lockedEnd),
    );
    if (matched?.id) {
      return {
        date: lockedDate,
        deliveryTimeId: Number(matched.id),
      };
    }
  }

  return null;
}

export const getDistrictRemediationLockedParams = () => {
  const state = getDistrictRemediationState();
  if (!state?.active) return null;
  return {
    districtId: state.targetDistrictId,
    date: state.lockedDate,
    deliveryTimeId: state.lockedDeliveryTimeId,
    deliveryTimeTitle: state.lockedDeliveryTimeTitle,
    deliveryTimeStart: state.lockedDeliveryTimeStart,
    deliveryTimeEnd: state.lockedDeliveryTimeEnd,
  };
};

export const resolveRemediationDeliveryParams = (
  deliveryData?: DeliveryData,
): {
  districtId: number;
  date: string;
  deliveryTimeId: number;
} | null => {
  const state = getDistrictRemediationState();
  if (!state?.active) return null;

  if (!deliveryData?.schemas?.length) {
    return null;
  }

  const resolvedSlot = resolveDeliverySlotFromData(
    deliveryData,
    state.lockedDate,
    state.lockedDeliveryTimeTitle,
    state.lockedDeliveryTimeStart,
    state.lockedDeliveryTimeEnd,
  );

  if (!resolvedSlot) {
    return null;
  }

  return {
    districtId: state.targetDistrictId,
    date: resolvedSlot.date,
    deliveryTimeId: resolvedSlot.deliveryTimeId,
  };
};

/** UI state fallback when delivery times loaded but slot object not yet in deliveryData snapshot. */
export function resolveRemediationDeliveryParamsFromSelection({
  districtId,
  date,
  deliveryTimeId,
}: {
  districtId?: number;
  date?: string;
  deliveryTimeId?: string | number;
}) {
  const state = getDistrictRemediationState();
  if (!state?.active) return null;
  if (districtId !== state.targetDistrictId) return null;
  const parsedTimeId = Number(deliveryTimeId);
  if (!date || !parsedTimeId || Number.isNaN(parsedTimeId)) return null;
  return {
    districtId: state.targetDistrictId,
    date,
    deliveryTimeId: parsedTimeId,
  };
}
